import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  FormControl,
  Button,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from "@mui/material";
import { AttachFile, Edit, Delete } from "@mui/icons-material";
import FormContainer from "../../../../../../components/Forms/FormContainer";
import useDecodedUser from "../../../../../../services/hooks/useDecodedUser";
import { ExecutePostWithParams, GetDataFromApiWithParams, ExecuteApiToPost, UpdateAndGetResponse } from "../../../../../../services/api/ExecuteApiRequests";

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function RegisterOPRequest() {
  const decodedUser = useDecodedUser();
  const [tabValue, setTabValue] = useState(0);
  
  // Registration form states
  const [requestNumbers, setRequestNumbers] = useState([]);
  const [buildingPermitNo, setBuildingPermitNo] = useState("");
  const [requestDate, setRequestDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [requestFormAttachedPath, setRequestFormAttachedPath] = useState(null);
  const [description, setDescription] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [responseError, setResponseError] = useState("");
  const [responseSuccess, setResponseSuccess] = useState("");
  
  // Edit form states
  const [occupancyPermits, setOccupancyPermits] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [editId, setEditId] = useState("");
  const [editBuildingPermitNo, setEditBuildingPermitNo] = useState("");
  const [editRequestDate, setEditRequestDate] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editAttachedFile, setEditAttachedFile] = useState(null);
  const [editExistingFile, setEditExistingFile] = useState(null);
  const [editResponseMessage, setEditResponseMessage] = useState("");
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [fetchSuccess, setFetchSuccess] = useState("");

  // Fetch building permits for dropdown
  useEffect(() => {
    const fetchBuildingPermit = async () => {
      try {
        setLoading(true);
        setFetchError("");
        const resultData = await GetDataFromApiWithParams("buildingPermits");
        console.log("Building permits fetched:", resultData);
        setRequestNumbers(resultData || []);
        // setFetchSuccess("Building permits loaded successfully!");
        setTimeout(() => setFetchSuccess(""), 3000);
      } catch (error) {
        console.log(error);
        setRequestNumbers([]);
        setFetchError("Error fetching building permits: " + (error.message || "Unknown error"));
      } finally {
        setLoading(false);
      }
    };
    fetchBuildingPermit();
  }, []);

  // Fetch occupancy permits for edit tab
  useEffect(() => {
    if (tabValue === 1) {
      fetchOccupancyPermits();
    }
  }, [tabValue]);

  const fetchOccupancyPermits = async () => {
    try {
      setLoading(true);
      setFetchError("");
      const resultData = await GetDataFromApiWithParams("occupancyPermits/getoccupancypermit");
      console.log("Occupancy permits fetched:", resultData);
      setOccupancyPermits(Array.isArray(resultData) ? resultData : []);
      // if (Array.isArray(resultData) && resultData.length > 0) {
      //   setFetchSuccess(`Loaded ${resultData.length} occupancy permits`);
      //   setTimeout(() => setFetchSuccess(""), 3000);
      // }
    } catch (error) {
      console.log(error);
      setOccupancyPermits([]);
      setFetchError("Error fetching occupancy permits: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset edit mode when switching tabs
    setEditMode(false);
    setSelectedPermit(null);
    setEditResponseMessage("");
    setEditError("");
    setEditSuccess("");
  };

  // Registration form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setResponseError("");
      setResponseSuccess("");
      
      console.log("Permit no:", buildingPermitNo);
      
      if (!buildingPermitNo) {
        setResponseError("Please select a building permit number");
        return;
      }
      
      const formData = new FormData();
      formData.append("requested_on", requestDate);
      formData.append("building_permit_id", buildingPermitNo);
      formData.append("description", description);
      formData.append("request_registered_by", decodedUser?.full_name || "");
      formData.append("file", requestFormAttachedPath);
      
      const responseData = await ExecutePostWithParams("occupancyPermits", formData, true);
      if (responseData) {
        console.log(responseData);
        // Reset form
        setRequestDate(new Date().toISOString().slice(0, 10));
        setBuildingPermitNo("");
        setRequestFormAttachedPath(null);
        setDescription("");
        setResponseSuccess("Occupancy Permit Registration successful!");
        setTimeout(() => setResponseSuccess(""), 5000);
        
        // Refresh the list if on edit tab
        if (tabValue === 1) {
          fetchOccupancyPermits();
        }
      }
    } catch (error) {
      console.log(error);
      setResponseError("Error submitting form: " + (error.message || "Unknown error"));
      setTimeout(() => setResponseError(""), 5000);
    }
  };

  // Edit form submit
  const handleEditSubmit = async (e) => {
    console.log("========== EDIT SUBMIT STARTED ==========");
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) return;
    
    // Clear previous messages
    setEditError("");
    setEditSuccess("");
    
    // Check if any values are undefined or null
    if (!editId) {
      console.error("editId is missing!");
      setEditError("No record selected for editing");
      return;
    }
    
    if (!editRequestDate) {
      console.error("editRequestDate is missing!");
      setEditError("Request date is required");
      return;
    }
    
    if (!editBuildingPermitNo) {
      console.error("editBuildingPermitNo is missing!");
      setEditError("Building permit number is required");
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Create FormData and append values
      const formData = new FormData();
      formData.append("id", editId);
      formData.append("requested_on", editRequestDate); 
      formData.append("building_permit_id", editBuildingPermitNo);
      formData.append("description", editDescription || "");
      formData.append("request_registered_by", decodedUser?.full_name || "");
      
      const formDataObject = Object.fromEntries(formData.entries());
      console.log("FormData as object:", formDataObject);
      
      // Only append file if a new one is selected
      if (editAttachedFile) {
        formData.append("file", editAttachedFile);
        console.log("  - file appended:", editAttachedFile.name, "size:", editAttachedFile.size);
      } else {
        console.log("  - No file to append");
      }
      
      // Make sure editId is properly formatted
      const endpoint = `occupancyPermits/edit/${editId}`;
      console.log("API Endpoint:", endpoint);
      
      // Log the actual fetch request that will be made
      console.log("Sending request to:", endpoint);
      console.log("FORM DATA", formData);
      
      const responseData = await UpdateAndGetResponse(endpoint, formData, true);
      
      if (responseData) {
        console.log("Response data:", responseData);
        setEditSuccess("Occupancy Permit updated successfully!");
        
        // Exit edit mode after a short delay to show success message
        setTimeout(() => {
          setEditMode(false);
          setSelectedPermit(null);
          fetchOccupancyPermits();
          
          // Clear edit form
          setEditId("");
          setEditBuildingPermitNo("");
          setEditRequestDate("");
          setEditDescription("");
          setEditAttachedFile(null);
          setEditExistingFile(null);
          setSubmitting(false);
          setEditSuccess("");
        }, 1500);
      }
    } catch (error) {
      console.log("Error in handleEditSubmit:", error);
      setEditError("Error updating record: " + (error.message || "Unknown error"));
      setSubmitting(false);
      setTimeout(() => setEditError(""), 5000);
    }
    console.log("========== EDIT SUBMIT ENDED ==========");
  };

  // Handle edit button click
  const handleEditClick = (permit) => {
    console.log("========== EDIT CLICKED ==========");
    console.log("Full permit object:", permit);
    
    // Clear messages
    setEditError("");
    setEditSuccess("");
    
    // Set the selected permit
    setSelectedPermit(permit);
    
    // Set edit ID
    setEditId(permit.id);
    console.log("Edit ID set to:", permit.id);
    
    // Set building permit number - ensure it's a string
    const buildingPermitId = permit.building_permit_id ? String(permit.building_permit_id) : "";
    setEditBuildingPermitNo(buildingPermitId);
    console.log("Edit Building Permit No set to:", buildingPermitId);
    
    // Set request date - format properly for date input (YYYY-MM-DD)
    if (permit.requested_on) {
      let formattedDate = "";
      
      // Handle different date formats
      if (permit.requested_on.includes('T')) {
        formattedDate = permit.requested_on.split('T')[0];
      } else if (permit.requested_on.includes('-')) {
        formattedDate = permit.requested_on.substring(0, 10);
      } else {
        try {
          const date = new Date(permit.requested_on);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split('T')[0];
          }
        } catch (e) {
          formattedDate = permit.requested_on;
        }
      }
      
      setEditRequestDate(formattedDate);
      console.log("Edit Request Date set to:", formattedDate);
    } else {
      setEditRequestDate("");
      console.log("Edit Request Date set to: empty (no date provided)");
    }
    
    // Set description
    setEditDescription(permit.description || "");
    console.log("Edit Description set to:", permit.description || "");
    
    // Store existing file info if available
    if (permit.file_path) {
      setEditExistingFile({
        name: permit.file_name || "Existing file",
        path: permit.file_path
      });
    } else {
      setEditExistingFile(null);
    }
    
    // Clear any previous file selection
    setEditAttachedFile(null);
    
    // Set edit mode to true
    setEditMode(true);
    console.log("Edit Mode set to: true");
  };

  // Handle delete button click
  const handleDeleteClick = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        setEditError("");
        setEditSuccess("");
        // Add your delete API call here
        // await ExecuteApiDelete(`occupancyPermits/${id}`);
        setEditSuccess(`Record ${id} deleted successfully!`);
        setTimeout(() => setEditSuccess(""), 3000);
        fetchOccupancyPermits();
      } catch (error) {
        console.log(error);
        setEditError("Error deleting record: " + (error.message || "Unknown error"));
        setTimeout(() => setEditError(""), 5000);
      }
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedPermit(null);
    setEditError("");
    setEditSuccess("");
    // Clear edit form
    setEditId("");
    setEditBuildingPermitNo("");
    setEditRequestDate("");
    setEditDescription("");
    setEditAttachedFile(null);
    setEditExistingFile(null);
  };

  if (loading && tabValue === 1 && !editMode) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading occupancy permits...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ width: '75%', mx: 'auto' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="occupancy permit tabs">
            <Tab label="Register New Occupancy Permit" />
            <Tab label="Edit Occupancy Permits" />
          </Tabs>
        </Box>
        
        {/* Global Messages */}
        {fetchError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFetchError("")}>
            {fetchError}
          </Alert>
        )}
        
        {fetchSuccess && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setFetchSuccess("")}>
            {fetchSuccess}
          </Alert>
        )}
        
        {/* Tab 1: Registration Form */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ width: '100%', mx: 'auto', mt: 2 }}>
            {responseError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setResponseError("")}>
                {responseError}
              </Alert>
            )}
            
            {responseSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResponseSuccess("")}>
                {responseSuccess}
              </Alert>
            )}
            
            <FormContainer title={"Occupancy Permit Registration"} onSubmit={handleSubmit}>
              <Grid item xs={12}>
                <TextField
                  label="Request Date"
                  type="date"
                  value={requestDate}
                  onChange={(e) => setRequestDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Building Permit Number</InputLabel>
                  <Select
                    value={buildingPermitNo}
                    onChange={(e) => setBuildingPermitNo(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select Building Permit</em>
                    </MenuItem>
                    {requestNumbers && requestNumbers.length > 0 ? (
                      requestNumbers.map((num) => {
                        const numId = String(num?.id || '');
                        return (
                          <MenuItem key={numId} value={numId}>
                            Building ID:- {num?.id} Agreement Id:- {num.agreement_id}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem disabled>No building permits available</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                <Button 
                    variant="contained" 
                    component="label" 
                    startIcon={<AttachFile />} 
                    fullWidth
                    sx={{ 
                      backgroundColor: '#D0EBD0',
                      color: '#2e7d32',
                      '&:hover': {
                        backgroundColor: '#C0DBC0',
                      },
                      textTransform: 'none'
                    }}
                  >
                    Attach Request Form
                    <input
                      type="file"
                      accept=".pdf, .docx, .doc, .jpeg, .jpg, .png"
                      onChange={(e) => setRequestFormAttachedPath(e.target.files[0])}
                      style={{ display: "none" }}
                    />
                  </Button>
                  {requestFormAttachedPath && (
                    <p style={{ marginTop: 8, fontSize: '0.875rem' }}>
                      Selected file: {requestFormAttachedPath.name}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Comments"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
            </FormContainer>
          </Box>
        </TabPanel>
        
        {/* Tab 2: Edit Form */}
        <TabPanel value={tabValue} index={1}>
          {!editMode ? (
            // List View
            <Card>
              <CardContent>
                {editError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError("")}>
                    {editError}
                  </Alert>
                )}
                
                {editSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEditSuccess("")}>
                    {editSuccess}
                  </Alert>
                )}
                
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                  <TableContainer sx={{ maxHeight: 650 }}>
                    <Table stickyHeader aria-label="occupancy permits table">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Building Permit ID</TableCell>
                          <TableCell>Request Date</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <CircularProgress size={30} />
                              <Typography variant="body2" sx={{ mt: 1 }}>Loading...</Typography>
                            </TableCell>
                          </TableRow>
                        ) : occupancyPermits.length > 0 ? (
                          occupancyPermits.map((permit) => (
                            <TableRow key={permit.id} hover>
                              <TableCell>{permit.id}</TableCell>
                              <TableCell>{permit.building_permit_id}</TableCell>
                              <TableCell>
                                {permit.requested_on ? new Date(permit.requested_on).toLocaleDateString() : 'N/A'}
                              </TableCell>
                              <TableCell>{permit.description || 'N/A'}</TableCell>
                              <TableCell>
                                <IconButton 
                                  color="primary" 
                                  onClick={() => handleEditClick(permit)}
                                  size="small"
                                  title="Edit"
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton 
                                  color="error" 
                                  onClick={() => handleDeleteClick(permit.id)}
                                  size="small"
                                  title="Delete"
                                  disabled
                                  sx={{
                                    '&.Mui-disabled': {
                                      '& .MuiSvgIcon-root': {
                                        color: 'gray',
                                      },
                                    },
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body1" color="textSecondary">
                                No occupancy permits found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </CardContent>
            </Card>
          ) : (
            // Edit Form
            <Paper key={editId} sx={{ p: 3, mb: 3, width: '100%', mx: 'auto', mt: 2 }}>
              {editError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError("")}>
                  {editError}
                </Alert>
              )}
              
              {editSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEditSuccess("")}>
                  {editSuccess}
                </Alert>
              )}
              
              <form onSubmit={handleEditSubmit}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: '#000000', textAlign: 'center' }}>
                  Edit Occupancy Permit (ID: {editId})
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Request Date"
                      type="date"
                      value={editRequestDate || ''}
                      onChange={(e) => setEditRequestDate(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Building Permit Number</InputLabel>
                      <Select
                        value={editBuildingPermitNo || ''}
                        onChange={(e) => setEditBuildingPermitNo(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Select Building Permit</em>
                        </MenuItem>
                        {requestNumbers && requestNumbers.length > 0 ? (
                          requestNumbers.map((num) => {
                            const numId = String(num?.id || '');
                            return (
                              <MenuItem key={numId} value={numId}>
                                Building ID:- {num?.id} Agreement Id:- {num.agreement_id}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem disabled>No building permits available</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                    <Button 
                    variant="contained" 
                    component="label" 
                    startIcon={<AttachFile />} 
                    fullWidth
                    sx={{ 
                      backgroundColor: '#D0EBD0',
                      color: '#2e7d32',
                      '&:hover': {
                        backgroundColor: '#C0DBC0',
                      },
                      textTransform: 'none'
                    }}
                  >
                    Attach Request Form
                    <input
                      type="file"
                      accept=".pdf, .docx, .doc, .jpeg, .jpg, .png"
                      onChange={(e) => setRequestFormAttachedPath(e.target.files[0])}
                      style={{ display: "none" }}
                    />
                  </Button>
                      
                      {/* Show existing file info */}
                      {editExistingFile && !editAttachedFile && (
                        <p style={{ marginTop: 8, fontSize: '0.875rem', color: '#666' }}>
                          Current file: {editExistingFile.name}
                        </p>
                      )}
                      
                      {/* Show newly selected file */}
                      {editAttachedFile && (
                        <p style={{ marginTop: 8, fontSize: '0.875rem', color: 'green' }}>
                          New file selected: {editAttachedFile.name}
                        </p>
                      )}
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Comments/ Description"
                      value={editDescription || ''}
                      onChange={(e) => setEditDescription(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleCancelEdit}
                        sx={{ minWidth: '120px' }}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        sx={{ 
                          minWidth: '120px',
                          backgroundColor: '#e0e0e0',
                          color: '#000000',
                          '&:hover': {
                            backgroundColor: '#d5d5d5',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#f5f5f5',
                            color: '#999999',
                          }
                        }}
                        disabled={submitting}
                      >
                        {submitting ? <CircularProgress size={24} /> : "Update"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          )}
        </TabPanel>
      </Box>
    </>
  );
}